import { ChangeEvent, forwardRef, memo, useCallback, useState } from "react";
import { number } from "card-validator";
import { formattingCardNumber, getPaymentMethodIconByCardType } from "../../../CreditCard/utils";
import { InputContainer, InputContainerProps } from "../../index";

export const defaultCardType = "unknown";
export const defaultCardMaxLength = 19;
export const defaultGaps = [ 4, 8, 12 ];

export type CardNumberFieldProps = Omit<InputContainerProps, "inputComponent" | "onChange" | "value"> & {
  onChange?: (value: string | null, e?: ChangeEvent<HTMLInputElement> | null) => void,
  value?: string | null,
}

export const CardNumberField = memo(forwardRef<HTMLInputElement, CardNumberFieldProps>((
  {
    onChange,
    value,
    ...props
  }, ref) => {
  const [ cardType, setCardType ] = useState<string>(defaultCardType);
  const [ gaps, setGaps ] = useState<number[]>(defaultGaps);
  const [ cardMaxLength, setCardMaxLength ] = useState<number>(defaultCardMaxLength);

  const wrappedOnChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const newValue = (e?.target?.value || "").replace(/\D/g, "");
    const cardNumberVerification = number(newValue);
    if (cardNumberVerification.isPotentiallyValid) {
      setCardType(cardNumberVerification.card?.type || defaultCardType);
      setGaps(cardNumberVerification?.card?.gaps || defaultGaps);
      setCardMaxLength(
        Math.max(...cardNumberVerification.card?.lengths || [ defaultCardMaxLength ]) +
        (cardNumberVerification.card?.gaps?.length || 0),
      );
    } else {
      setCardType(defaultCardType);
      setGaps(defaultGaps);
      setCardMaxLength(defaultCardMaxLength);
    }
    onChange?.(newValue, e);
  }, [ onChange ]);

  return <InputContainer
    autoComplete="cc-number"
    autoCorrect="off"
    spellCheck="false"
    inputMode="numeric"
    maxLength={cardMaxLength}
    {...props}
    startAdornment={getPaymentMethodIconByCardType(cardType)}
    value={formattingCardNumber(value || "", gaps)}
    ref={ref}
    type="text"
    onChange={wrappedOnChange}
  />
}));